/**
 * Job-change suggestions (SC-13586): "Jane may have moved to Globex — what should we do?"
 *
 * Dual-mode (Pattern 00): preview returns DUMMY_CONTACT_CHANGE_SUGGESTIONS and every query is
 * skipped; mutations resolve locally. Production reads the coffee API.
 *
 * Every query takes an `enabled` flag and skips without it. The person sheet is always mounted,
 * so a query gated only on an id would fire for every person anyone ever glances at.
 *
 * `snapshot` is a graphene JSONString — it crosses as a JSON *string*, not an object (the trap
 * that once made the bell render raw i18n keys). `parseSnapshot` is the one place it is read.
 */

import { useCallback } from "react";

import {
  DUMMY_CONTACT_CHANGE_IMPACT,
  DUMMY_CONTACT_CHANGE_SUGGESTIONS,
} from "@/data/dummy-contact-change-suggestions";
import {
  useAcceptContactChangeSuggestionMutation,
  useContactChangeImpactQuery,
  useContactChangeSuggestionQuery,
  useDismissContactChangeSuggestionMutation,
  usePendingContactChangeSuggestionQuery,
  useReopenContactChangeSuggestionMutation,
  type ContactChangeSuggestionFieldsFragment,
} from "@/lib/graphql/types-and-hooks";
import { useWorkspaceId } from "@/lib/hooks/use-workspace-id";
import { isLovablePreview } from "@/lib/utils/environment";

export const ACCEPT_MODES = ["CREATE_NEW", "LINK_EXISTING", "MOVE", "TITLE_ONLY"] as const;
export type AcceptMode = (typeof ACCEPT_MODES)[number];

export type SuggestionKind = "COMPANY_CHANGED" | "TITLE_CHANGED";

/** Frozen at detection; display only. Accept recomputes against live data server-side. */
export interface SuggestionSnapshot {
  personName?: string;
  fromEmail?: string | null;
  toEmail?: string | null;
  fromTitle?: string | null;
  toTitle?: string | null;
  fromCompany?: string | null;
  toCompany?: string | null;
  fromCompanyDomain?: string | null;
  toCompanyDomain?: string | null;
  fromStartDate?: string | null;
  toStartDate?: string | null;
  linkedinUrl?: string | null;
}

export interface ContactChangeSuggestion {
  id: string;
  kind: SuggestionKind;
  status: "pending" | "accepted" | "dismissed" | "superseded" | "expired";
  contactId: string;
  snapshot: SuggestionSnapshot;
  availableModes: AcceptMode[];
  acceptMode: AcceptMode | null;
  actedByUserId: number | null;
  actedByName: string | null;
  actedTs: string | null;
  resolvedContactId: string | null;
  dismissedByViewer: boolean;
  createTs: string | null;
}

export interface ContactChangeImpact {
  engagementCount: number | null;
  dealNames: string[];
  dealCount: number;
  existingContactId: string | null;
  existingContactName: string | null;
  accountMatch: "found" | "none" | "ambiguous";
  accountCandidates: { id: string; name: string | null; domain: string | null }[];
  syncedTo: string[];
}

/** Stable codes from the backend; the dialog branches on these, never on message text. */
export type SuggestionErrorCode =
  | "ALREADY_RESOLVED"
  | "NOT_FOUND"
  | "INVALID_MODE"
  | "CONTACT_GONE"
  | "SOURCE_GONE"
  | "EXISTING_CONTACT_AT_NEW_EMAIL"
  | "NO_EXISTING_CONTACT"
  | "NEEDS_COMPANY"
  | "SUPERSEDED";

export interface SuggestionActionResult {
  ok: boolean;
  errorCode: SuggestionErrorCode | null;
  errorMessage: string | null;
  suggestion: ContactChangeSuggestion | null;
  resolvedContactId: string | null;
}

export function parseSnapshot(raw: unknown): SuggestionSnapshot {
  if (!raw) return {};
  if (typeof raw === "object") return raw as SuggestionSnapshot;
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === "object" ? (parsed as SuggestionSnapshot) : {};
    } catch {
      return {};
    }
  }
  return {};
}

const isMode = (m: unknown): m is AcceptMode => ACCEPT_MODES.includes(m as AcceptMode);

export function toSuggestion(f: ContactChangeSuggestionFieldsFragment | null | undefined): ContactChangeSuggestion | null {
  if (!f?.id || !f.contactId) return null;
  return {
    id: f.id,
    kind: f.kind === "TITLE_CHANGED" ? "TITLE_CHANGED" : "COMPANY_CHANGED",
    status: (f.status ?? "pending") as ContactChangeSuggestion["status"],
    contactId: f.contactId,
    snapshot: parseSnapshot(f.snapshot),
    availableModes: (f.availableModes ?? []).filter(isMode),
    acceptMode: isMode(f.acceptMode) ? f.acceptMode : null,
    actedByUserId: f.actedByUserId ?? null,
    actedByName: f.actedByName ?? null,
    actedTs: f.actedTs ?? null,
    resolvedContactId: f.resolvedContactId ?? null,
    dismissedByViewer: Boolean(f.dismissedByViewer),
    createTs: f.createTs ?? null,
  };
}

/** The open question about one contact, for the person-sheet banner. Null when none. */
export function usePendingContactChangeSuggestion(contactId: string | null | undefined, enabled: boolean) {
  const workspaceId = useWorkspaceId();
  const preview = isLovablePreview();
  const { data, loading, refetch } = usePendingContactChangeSuggestionQuery({
    variables: { workspaceId: workspaceId ?? "", contactId: contactId ?? "" },
    skip: preview || !enabled || !workspaceId || !contactId,
    // A colleague may resolve it at any moment; never trust a cached "pending".
    fetchPolicy: "cache-and-network",
  });
  if (preview) {
    return { suggestion: enabled ? DUMMY_CONTACT_CHANGE_SUGGESTIONS["1"] : null, loading: false, refetch: async () => undefined };
  }
  return { suggestion: toSuggestion(data?.pendingContactChangeSuggestion), loading, refetch };
}

/** One suggestion by id, any status — the dialog's source of truth. */
export function useContactChangeSuggestion(id: string | null | undefined, enabled: boolean) {
  const workspaceId = useWorkspaceId();
  const preview = isLovablePreview();
  const { data, loading, error, refetch } = useContactChangeSuggestionQuery({
    variables: { workspaceId: workspaceId ?? "", id: id ?? "" },
    skip: preview || !enabled || !workspaceId || !id,
    fetchPolicy: "network-only",
  });
  if (preview) {
    return {
      suggestion: id ? DUMMY_CONTACT_CHANGE_SUGGESTIONS[id] ?? null : null,
      loading: false,
      error: undefined,
      refetch: async () => undefined,
    };
  }
  return { suggestion: toSuggestion(data?.contactChangeSuggestion), loading, error, refetch };
}

/** Live counts for the confirmation step. Fetched only when that step is reached. */
export function useContactChangeImpact(id: string | null | undefined, enabled: boolean) {
  const workspaceId = useWorkspaceId();
  const preview = isLovablePreview();
  const { data, loading } = useContactChangeImpactQuery({
    variables: { workspaceId: workspaceId ?? "", id: id ?? "" },
    skip: preview || !enabled || !workspaceId || !id,
    fetchPolicy: "network-only",
  });
  if (preview) return { impact: enabled ? DUMMY_CONTACT_CHANGE_IMPACT : null, loading: false };
  const raw = data?.contactChangeImpact;
  const impact: ContactChangeImpact | null = raw
    ? {
        engagementCount: raw.engagementCount ?? null,
        dealNames: (raw.dealNames ?? []).filter((n): n is string => Boolean(n)),
        dealCount: raw.dealCount ?? 0,
        existingContactId: raw.existingContactId ?? null,
        existingContactName: raw.existingContactName ?? null,
        accountMatch: raw.accountMatch === "found" || raw.accountMatch === "ambiguous" ? raw.accountMatch : "none",
        accountCandidates: (raw.accountCandidates ?? [])
          .filter((c): c is NonNullable<typeof c> => Boolean(c?.id))
          .map(c => ({ id: c.id as string, name: c.name ?? null, domain: c.domain ?? null })),
        syncedTo: (raw.syncedTo ?? []).filter((s): s is string => Boolean(s)),
      }
    : null;
  return { impact, loading };
}

export interface AcceptArgs {
  id: string;
  mode: AcceptMode;
  createCompany?: boolean;
  accountId?: string | null;
}

export function useContactChangeActions() {
  const workspaceId = useWorkspaceId();
  const [acceptMutation, { loading: accepting }] = useAcceptContactChangeSuggestionMutation();
  const [dismissMutation, { loading: dismissing }] = useDismissContactChangeSuggestionMutation();
  const [reopenMutation, { loading: reopening }] = useReopenContactChangeSuggestionMutation();

  const accept = useCallback(
    async ({ id, mode, createCompany = true, accountId = null }: AcceptArgs): Promise<SuggestionActionResult> => {
      if (isLovablePreview()) {
        const base = DUMMY_CONTACT_CHANGE_SUGGESTIONS[id] ?? null;
        return {
          ok: true, errorCode: null, errorMessage: null, resolvedContactId: base?.contactId ?? null,
          suggestion: base ? { ...base, status: "accepted", acceptMode: mode, actedByName: "You" } : null,
        };
      }
      const { data } = await acceptMutation({
        variables: { workspaceId: workspaceId ?? "", id, mode, createCompany, accountId },
      });
      const r = data?.acceptContactChangeSuggestion;
      return {
        ok: Boolean(r?.ok),
        errorCode: (r?.errorCode as SuggestionErrorCode | null) ?? null,
        errorMessage: r?.errorMessage ?? null,
        suggestion: toSuggestion(r?.suggestion),
        resolvedContactId: r?.resolvedContactId ?? null,
      };
    },
    [acceptMutation, workspaceId],
  );

  const dismiss = useCallback(
    async (id: string): Promise<SuggestionActionResult> => {
      if (isLovablePreview()) {
        const base = DUMMY_CONTACT_CHANGE_SUGGESTIONS[id] ?? null;
        return {
          ok: true, errorCode: null, errorMessage: null, resolvedContactId: null,
          suggestion: base ? { ...base, dismissedByViewer: true } : null,
        };
      }
      const { data } = await dismissMutation({ variables: { workspaceId: workspaceId ?? "", id } });
      const r = data?.dismissContactChangeSuggestion;
      return {
        ok: Boolean(r?.ok),
        errorCode: (r?.errorCode as SuggestionErrorCode | null) ?? null,
        errorMessage: r?.errorMessage ?? null,
        suggestion: toSuggestion(r?.suggestion),
        resolvedContactId: null,
      };
    },
    [dismissMutation, workspaceId],
  );

  /** Undo the viewer's own "Not now". */
  const reopen = useCallback(
    async (id: string): Promise<SuggestionActionResult> => {
      if (isLovablePreview()) {
        const base = DUMMY_CONTACT_CHANGE_SUGGESTIONS[id] ?? null;
        return {
          ok: true, errorCode: null, errorMessage: null, resolvedContactId: null,
          suggestion: base ? { ...base, status: "pending", dismissedByViewer: false } : null,
        };
      }
      const { data } = await reopenMutation({ variables: { workspaceId: workspaceId ?? "", id } });
      const r = data?.reopenContactChangeSuggestion;
      return {
        ok: Boolean(r?.ok),
        errorCode: (r?.errorCode as SuggestionErrorCode | null) ?? null,
        errorMessage: r?.errorMessage ?? null,
        suggestion: toSuggestion(r?.suggestion),
        resolvedContactId: null,
      };
    },
    [reopenMutation, workspaceId],
  );

  return { accept, dismiss, reopen, busy: accepting || dismissing || reopening };
}
